import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  Signal,
} from '@angular/core';

import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Slider } from 'primeng/slider';
import { Nillable, valueOrDefault } from '@web/utils';
import { Button } from 'primeng/button';
import {
  FormControlComponent,
  PrimengButtonDirective,
  WarningComponent,
} from '@web/shared/ui';

import { toSignal } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs';
import {
  AdminSubscriptionForm,
  AdminSubscriptionFormGroup,
} from '../../models/admin-subscription-form';

@Component({
  selector: 'app-admin-subscription-form',
  imports: [
    ReactiveFormsModule,
    Slider,
    Button,
    PrimengButtonDirective,
    FormControlComponent,
    WarningComponent,
  ],
  templateUrl: './admin-subscription-form.component.html',
  styleUrl: './admin-subscription-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminSubscriptionFormComponent implements OnInit {
  public readonly data = input<Nillable<AdminSubscriptionForm>>();
  public readonly submitClickEvent = output<AdminSubscriptionForm>();

  protected readonly minRangeValue = 5;
  protected readonly maxRangeValue = 15;
  protected readonly initialValue = 5;
  protected readonly form: AdminSubscriptionFormGroup;
  protected readonly currentCardChargeDay: Signal<number>;
  protected readonly shouldShowStandardDayMessage: Signal<boolean>;
  protected readonly shouldShowConcreteDayMessage: Signal<boolean>;
  protected readonly shouldShowNextMonthMessage: Signal<boolean>;

  private readonly builder = inject(FormBuilder);

  /* prettier-ignore */
  public constructor() {
    this.form = this.buildForm();
    this.currentCardChargeDay = this.prepareCurrentCardChargeDaySignal(this.form);
    this.shouldShowStandardDayMessage = this.prepareShouldShowStandardDayMessage();
    this.shouldShowConcreteDayMessage = this.prepareShouldShowConcreteDayMessage();
    this.shouldShowNextMonthMessage = this.prepareShouldShowNextMonthMessage();
  }

  public ngOnInit(): void {
    this.tryToFillForm();
  }

  public handleSubmitButtonClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  /* prettier-ignore */
  private buildForm(): AdminSubscriptionFormGroup {
    return this.builder.group({
      cardChargeDay: this.builder.nonNullable.control<number>(this.initialValue, [Validators.required]),
    });
  }

  private prepareCurrentCardChargeDaySignal(
    form: AdminSubscriptionFormGroup,
  ): Signal<number> {
    const control = form.controls.cardChargeDay;

    return toSignal(control.valueChanges.pipe(startWith(control.value)), {
      initialValue: control.value,
    });
  }

  private tryToFillForm(): void {
    const data = this.data();

    this.form.patchValue({
      cardChargeDay: valueOrDefault(data?.cardChargeDay, this.initialValue),
    });
  }

  private toData(): AdminSubscriptionForm {
    const data = this.form.getRawValue();

    return new AdminSubscriptionForm({
      cardChargeDay: data.cardChargeDay,
    });
  }

  private prepareShouldShowStandardDayMessage(): Signal<boolean> {
    return computed(() => this.currentCardChargeDay() === 5);
  }

  private prepareShouldShowConcreteDayMessage(): Signal<boolean> {
    return computed(() => this.currentCardChargeDay() > 5);
  }

  private prepareShouldShowNextMonthMessage(): Signal<boolean> {
    return computed(() => this.currentCardChargeDay() < new Date().getDate());
  }
}
